import { Search, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ProfileAvatar from "@/components/ProfileAvatar";
import { useIsMobile } from "@/hooks/use-mobile";

interface TopBarProps {
  onMobileMenuToggle?: () => void;
}

export function TopBar({ onMobileMenuToggle }: TopBarProps) {
  const { user, signOut } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <div className="sticky top-0 z-20 w-full h-16 border-b border-zinc-200 dark:border-zinc-700 bg-background/80 dark:bg-zinc-800/80 backdrop-blur-sm">
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMobileMenuToggle}
              className="h-8 w-8"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          
          {/* Search - responsive width */}
          <div className="relative w-full max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-500 dark:text-zinc-400" />
            <Input
              type="search"
              placeholder={isMobile ? "Search..." : "Search projects, documents, and content..."}
              className="w-full pl-9 bg-zinc-50 dark:bg-zinc-700/50 border-zinc-200 dark:border-zinc-700"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative rounded-full p-0 h-auto">
                <ProfileAvatar size="sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-primary line-clamp-1">
                  {user?.email || 'User'}
                </p>
                <p className="text-xs leading-4 text-zinc-500 dark:text-zinc-400 line-clamp-1">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => {
                if (id) {
                  localStorage.setItem('currentProjectId', id);
                }
                navigate('/settings');
              }}>
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => {
                if (id) {
                  navigate(`/projects/${id}/subscription`);
                } else {
                  const currentProjectId = localStorage.getItem('currentProjectId');
                  if (currentProjectId) {
                    navigate(`/projects/${currentProjectId}/subscription`);
                  } else {
                    navigate('/projects');
                  }
                }
              }}>
                Subscription & Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <ThemeToggle showLabel={true} />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => signOut()} className="text-red-600 dark:text-red-400">
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
